import { ToastProvider, ToastViewport } from "@tamagui/toast";
import { UserRole } from "common";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
} from "react-router";
import { TamaguiProvider } from "tamagui";
import tamaguiConfig from "tamagui-config";

import { CurrentToast } from "./components/CurrentToast";
import Layout from "./components/Layout";
import { AppSyncProvider } from "./contexts/AppSyncContext";
import {
  UserRoleProvider,
  useUserRoleContext,
} from "./contexts/UserRoleContext";
import AlertDetails from "./pages/Alerts/AlertDetails";
import Alerts from "./pages/Alerts/Alerts";
import Analytics from "./pages/Analytics/Analytics";
import BaseStations from "./pages/BaseStations/BaseStations";
import CreateCustomer from "./pages/Customers/CreateCustomer";
import CustomerList from "./pages/Customers/CustomerList";
import { CustomerView } from "./pages/Customers/CustomerView";
import Dashboard from "./pages/Dashboard";
import ForgotPassword from "./pages/ForgotPassword";
import ForgotPasswordConfirmation from "./pages/ForgotPasswordConfirmation";
import Login from "./pages/Login";
import OAuthCallback from "./pages/OAuthCallback";
import ResetPassword from "./pages/ResetPassword";
import ResetPasswordConfirmation from "./pages/ResetPasswordConfirmation";
import SensorEvents from "./pages/SensorEvents";
import Sensors from "./pages/Sensors/Sensors";
import ServiceRequestDetail from "./pages/ServiceRequests/ServiceRequestDetail";
import ServiceRequestForm from "./pages/ServiceRequests/ServiceRequestForm";
import ServiceRequestsList from "./pages/ServiceRequests/ServiceRequestsList";
import GeneralSettings from "./pages/Settings/GeneralSettings";
import Help from "./pages/Settings/Help";
import Integrations from "./pages/Settings/Integrations";
import SensorManuals from "./pages/Settings/SensorManuals";
import ServiceContact from "./pages/Settings/ServiceContact";
import TwoFactorAuthentication from "./pages/Settings/TwoFactorAuthentication";
import Weather from "./pages/Weather";

import "./i18n";

function App() {
  return (
    <UserRoleProvider>
      <TamaguiProvider config={tamaguiConfig} defaultTheme="dark">
        <AppSyncProvider>
          <ToastProvider>
            <Router>
              <Routes>
                <Route
                  path="/login"
                  element={
                    <GuestRoute>
                      <Login />
                    </GuestRoute>
                  }
                />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route
                  path="/forgot-password-confirmation"
                  element={<ForgotPasswordConfirmation />}
                />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/reset-password-confirmation"
                  element={<ResetPasswordConfirmation />}
                />
                <Route path="/oauth-callback" element={<OAuthCallback />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/weather"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Weather />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Analytics />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <GeneralSettings />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/integrations"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Integrations />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/service-contact"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ServiceContact />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sensor-manuals"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SensorManuals />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/help"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Help />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/two-factor-authentication"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <TwoFactorAuthentication />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/alerts"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Alerts />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/alerts/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <AlertDetails />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/base-stations"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <BaseStations />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/base-stations/:hubId/sensors"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Sensors />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/base-stations/:hubId/sensors/:sensorId/events"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SensorEvents />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/service-requests"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ServiceRequestsList />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/service-requests/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ServiceRequestDetail />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/service-requests/new"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ServiceRequestForm />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/service-requests/edit/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ServiceRequestForm />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CustomerList />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers/new"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CreateCustomer />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CustomerView />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<RedirectHome />} />
              </Routes>
            </Router>

            <CurrentToast />
            <ToastViewport
              flexDirection="column"
              bottom="$5"
              left="$5"
              right="$5"
            />
          </ToastProvider>
        </AppSyncProvider>
      </TamaguiProvider>
    </UserRoleProvider>
  );
}

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const {
    refreshUserRole,
    role,
    isInitialized: isUserRoleInitialized,
    isRefreshingUserRole,
  } = useUserRoleContext();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    refreshUserRole?.();
  }, [navigate]);

  useEffect(() => {
    if (isUserRoleInitialized && !isRefreshingUserRole) {
      setIsAuthenticated(!!role && role !== UserRole.UNAUTHENTICATED);
      setIsLoading(false);
    }
  }, [isUserRoleInitialized, isRefreshingUserRole]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const GuestRoute = ({ children }: { children: ReactNode }) => {
  const {
    refreshUserRole,
    role,
    isInitialized: isUserRoleInitialized,
    isRefreshingUserRole,
  } = useUserRoleContext();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    refreshUserRole?.();
  }, [navigate]);

  useEffect(() => {
    if (isUserRoleInitialized && !isRefreshingUserRole) {
      setIsAuthenticated(!!role && role !== UserRole.UNAUTHENTICATED);
      setIsLoading(false);
    }
  }, [isUserRoleInitialized, isRefreshingUserRole]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const RedirectHome = () => {
  const { isInitialized, homeRoute } = useUserRoleContext();

  if (!isInitialized) {
    return <LoadingIndicator />;
  }

  return <Navigate to={homeRoute} replace />;
};

const LoadingIndicator = () => {
  const { t } = useTranslation();

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        fontSize: "18px",
      }}
    >
      {t("common.loading")}
    </div>
  );
};

export default App;
